<template>
  <figure class="cathay-flex-film">
    <div class="cathay-flex-film-frame">
      <video ref="video" class="cathay-flex-film-video" :src="started ? videoSource.src : undefined" :poster="videoSource.poster" :controls="started && controlsVisible" :aria-hidden="!started" :tabindex="started ? 0 : -1" :preload="started ? 'metadata' : 'none'" playsinline aria-label="Cathay Flex introduction" @play="onPlay" @pause="onPause" @ended="onEnded" @error="onError" />
      <button v-if="!started" ref="playButton" class="cathay-flex-film-play cathay-flex-cover-trigger" type="button" aria-label="Play the Cathay Flex video, 2 minutes" @click="play">
        <CathayFlexFilmCover />
      </button>
    </div>
    <figcaption v-if="failed" class="cathay-flex-film-error" role="status">
      The video could not play. <a :href="videoSource.src" target="_blank" rel="noopener">Open the video</a>
    </figcaption>
  </figure>
</template>

<script>
import CathayFlexFilmCover from './CathayFlexFilmCover.vue';
import { cathayFlexVideo } from '../../data/cathayFlexSources';
import { watchVideoControls } from '../../../public/slidepoise/docs/site/video-controls.mjs';
import '../../../public/slidepoise/docs/site/video-controls.css';

export default {
  name: 'CathayFlexFilm',
  components: { CathayFlexFilmCover },
  props: { active: { type: Boolean, default: true } },
  data: () => ({ started: false, failed: false, controlsVisible: true }),
  computed: { videoSource() { return cathayFlexVideo; } },
  watch: { active(value) { if (!value) this.pause(); } },
  mounted() {
    this._controlsCleanup = watchVideoControls(this.$refs.video, visible => { this.controlsVisible = visible; });
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('view-summary-play', this.otherAudio);
    this._observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !document.fullscreenElement && !this.$refs.video?.webkitDisplayingFullscreen) this.pause();
    });
    this._observer.observe(this.$el);
  },
  beforeUnmount() {
    this._controlsCleanup?.();
    this.pause();
    this._observer?.disconnect();
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('view-summary-play', this.otherAudio);
  },
  methods: {
    async play() {
      if (!this.active) return;
      this.started = true;
      this.failed = false;
      await this.$nextTick();
      const video = this.$refs.video;
      if (!this.active || !video) return;
      video.focus({ preventScroll: true });
      try { await video.play(); }
      catch (error) { if (error.name !== 'AbortError') this.failed = true; }
    },
    onPlay() {
      if (!this.active || document.hidden) { this.pause(); return; }
      this._announcedPlayback = true;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    onPause() {
      if (!this._announcedPlayback) return;
      this._announcedPlayback = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    onEnded() { this.onPause(); },
    onError() { this.failed = true; this.onPause(); },
    pause() { this.$refs.video?.pause(); this.onPause(); },
    otherAudio(event) { if (event.detail !== this) this.pause(); },
    visibilityChanged() { if (document.hidden) this.pause(); },
  },
};
</script>

<style scoped>
.cathay-flex-film{container-type:inline-size;width:100%;min-width:0;margin:0;color:#153e35;font:14px/1.4 'Quattrocento Sans',sans-serif}
.cathay-flex-film-frame{position:relative;aspect-ratio:16/9;overflow:hidden;background:#f8faf6}
.cathay-flex-film-video{display:block;width:100%;height:100%;object-fit:contain}
.cathay-flex-film-play{position:absolute;inset:0;display:block;width:100%;height:100%;padding:0;border:0;border-radius:0;background:#f8faf6;color:#153e35;cursor:var(--site-interactive-cursor,pointer);-webkit-tap-highlight-color:transparent}
.cathay-flex-film-play:focus-visible{outline:2px solid currentColor;outline-offset:-4px}
.cathay-flex-film .cathay-flex-film-error{display:block;min-height:0;margin:0;padding:8px 0;font:12px/1.4 'Quattrocento Sans',sans-serif;color:var(--site-text-secondary,#77756f)}
.cathay-flex-film-error a{color:inherit;text-underline-offset:3px}
</style>
